/**
 * Cart-level gift wrap option (snippets/gift-wrap-cart.liquid).
 * At most one gift wrap line (quantity 1) is kept in the cart, identified by the
 * '_sst_gift_wrap' property. Boxes live inside cart-form [data-merge] regions and are
 * re-rendered on every cart refresh, so all state is document-level here.
 */
(() => {
  const cartUrl = `${window.Shopify.routes.root}cart.js`;
  const cartChangeUrl = `${window.Shopify.routes.root}cart/change.js`;
  const headers = { 'Content-Type': 'application/json' };

  // In-flight requests. Module-scoped so they survive boxes being re-rendered mid-request.
  let addPromise = null;
  let removePromise = null;
  let messagePromise = Promise.resolve();
  let messageTimeout;

  const findGiftLine = (cart) => cart.items.find((item) => item.properties && item.properties._sst_gift_wrap === 'Yes');

  const getCart = () => fetch(cartUrl).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  });

  const postJson = (url, body) => fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  }).then((response) => {
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  });

  const dispatchCartChange = () => {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  const handleError = (error) => {
    // eslint-disable-next-line no-console
    console.log(error.message);
    document.dispatchEvent(new CustomEvent('on:cart:error', {
      bubbles: true,
      detail: {
        error: error.message
      }
    }));
  };

  /**
   * Show a box as checked/unchecked.
   * @param {Element} box - .gift-wrap-cart element.
   * @param {boolean} checked - Checked state.
   * @param {boolean} instant - Skip the open/close transition (used when syncing a re-rendered box).
   */
  const setBoxState = (box, checked, instant) => {
    const message = box.querySelector('.gift-wrap-cart__message');
    box.querySelector('.gift-wrap-cart__checkbox').checked = checked;
    if (instant) message.style.transition = 'none';
    message.classList.toggle('is-open', checked);
    if (instant) {
      message.offsetHeight; // eslint-disable-line no-unused-expressions
      message.style.transition = '';
    }
  };

  const getBoxes = () => document.querySelectorAll('.gift-wrap-cart');

  const removeGiftWrap = () => {
    clearTimeout(messageTimeout);
    const pending = Promise.all([addPromise, messagePromise]);
    const request = pending
      .then(getCart)
      .then((cart) => {
        const line = findGiftLine(cart);
        return line ? postJson(cartChangeUrl, { id: line.key, quantity: 0 }) : null;
      })
      .then(dispatchCartChange)
      .catch(handleError)
      .finally(() => {
        if (removePromise === request) removePromise = null;
      });
    removePromise = request;
  };

  const addGiftWrap = (box) => {
    const message = box.querySelector('.gift-wrap-cart__message').value.trim();
    const properties = { _sst_gift_wrap: 'Yes' };
    if (message) properties['Gift message'] = message;

    const request = Promise.resolve(removePromise)
      .then(() => postJson(theme.routes.cartAdd, {
        id: Number(box.dataset.variantId),
        quantity: 1,
        properties
      }))
      .then(dispatchCartChange)
      .catch((error) => {
        getBoxes().forEach((el) => setBoxState(el, false));
        handleError(error);
      })
      .finally(() => {
        if (addPromise === request) addPromise = null;
      });
    addPromise = request;
  };

  // Silent update: no on:cart:change, so the textarea is not re-rendered while typing.
  const updateMessage = async (message) => {
    await addPromise;
    if (removePromise) return;

    const line = findGiftLine(await getCart());
    if (!line || removePromise) return;

    const properties = { ...line.properties };
    if (message) {
      properties['Gift message'] = message;
    } else {
      delete properties['Gift message'];
    }

    // Changing properties re-keys the line; keep the rendered line's key current for CartForm.
    const cart = await postJson(cartChangeUrl, { id: line.key, quantity: 1, properties });
    const newLine = findGiftLine(cart);
    if (newLine) {
      document.querySelectorAll(`.cart-item__quantity-input[data-key="${line.key}"]`).forEach((el) => {
        el.dataset.key = newLine.key;
      });
    }
  };

  const resync = async () => {
    if (!getBoxes().length) return;

    // An uncheck is in flight: the cart may still contain the line, trust the user's intent.
    if (removePromise) {
      getBoxes().forEach((box) => setBoxState(box, false, true));
      return;
    }

    try {
      await addPromise;
      const cart = await getCart();
      if (removePromise) return;

      let line = findGiftLine(cart);
      if (line && cart.items.length === 1) {
        removeGiftWrap();
        line = null;
      }

      getBoxes().forEach((box) => {
        setBoxState(box, Boolean(line), true);
        const message = box.querySelector('.gift-wrap-cart__message');
        if (line && message !== document.activeElement) {
          message.value = line.properties['Gift message'] || '';
        }
      });
    } catch (error) {
      handleError(error);
    }
  };

  document.addEventListener('change', (evt) => {
    const checkbox = evt.target.closest('.gift-wrap-cart__checkbox');
    if (!checkbox) return;

    const box = checkbox.closest('.gift-wrap-cart');
    getBoxes().forEach((el) => setBoxState(el, checkbox.checked, el !== box));
    if (checkbox.checked) {
      addGiftWrap(box);
    } else {
      removeGiftWrap();
    }
  });

  document.addEventListener('input', (evt) => {
    const textarea = evt.target.closest('.gift-wrap-cart__message');
    if (!textarea) return;

    clearTimeout(messageTimeout);
    messageTimeout = setTimeout(() => {
      messagePromise = messagePromise
        .then(() => updateMessage(textarea.value.trim()))
        .catch(handleError);
    }, 500);
  });

  document.addEventListener('DOMContentLoaded', resync);
  document.addEventListener('on:cart:after-merge', resync);
  document.addEventListener('on:cart-drawer:before-open', resync);
})();
